'use client';

import { useMicrocopy } from '@contexts/MicrocopyContext';
import { MicrocopyFragment } from '@graphql/generated-contentful/graphql';
import { useCallback, useMemo } from 'react';
import { TreeAccordion } from 'ui-components';
import { FacetAccordion } from '../FacetAccordion';
import { FacetDropdown } from '../FacetDropdown';
import { DATE_RANGE_KEYS, getReleaseDateController } from '../facetControllers';
import { DateFlatFacetValue, useFlatFacet } from '../hooks/useFlatFacet';
import { ReleaseDateFacetProps } from './ReleaseDateFacet.types';

const getValueId = (v: DateFlatFacetValue): string => `${v.start}-${v.end}`;

export const ReleaseDateFacet = ({
  variant = 'horizontal',
  className,
  isOpen,
  onToggle,
}: ReleaseDateFacetProps) => {
  const { searchLabelsMicrocopy } = useMicrocopy<{
    searchLabelsMicrocopy: MicrocopyFragment[];
  }>();

  const dateLabel =
    searchLabelsMicrocopy?.find((m) => m.key === 'search.release-date.title')
      ?.value ?? 'Release Date';

  const clearLabel =
    searchLabelsMicrocopy?.find((m) => m.key === 'search.release-date.clear')
      ?.value ?? 'Clear';

  const {
    optimisticValues,
    selectedIds,
    selectedCount,
    handleClear,
    handleSelectMany,
  } = useFlatFacet({
    getController: getReleaseDateController,
    fieldName: 'first_published_at',
    getValueId,
  });

  const getLabel = useCallback(
    (index: number) => {
      const range = DATE_RANGE_KEYS[index];
      const entry = searchLabelsMicrocopy?.find((m) => m.key === range.key);
      return entry?.value ?? range.label;
    },
    [searchLabelsMicrocopy]
  );

  const isDark = variant === 'mobile';

  const treeItems = useMemo(
    () =>
      optimisticValues.map((v, index) => ({
        id: getValueId(v),
        label: getLabel(index),
        count: v.numberOfResults,
        disabled: v.numberOfResults === 0 && v.state !== 'selected',
      })),
    [optimisticValues, getLabel]
  );

  const content = (
    <TreeAccordion
      items={treeItems}
      selectionMode="multiple"
      selectedIds={selectedIds}
      onSelectMany={handleSelectMany}
      isDarkTheme={isDark}
      size={variant === 'sidebar' ? 'default' : 'sm'}
    />
  );

  if (variant === 'horizontal') {
    return (
      <FacetDropdown
        label={dateLabel}
        selectedCount={selectedCount}
        clearLabel={clearLabel}
        onClear={handleClear}
        className={className}
      >
        {content}
      </FacetDropdown>
    );
  }

  return (
    <FacetAccordion
      title={dateLabel}
      variant={variant}
      className={className}
      showClear={variant === 'drawer' && selectedCount > 0}
      onClear={handleClear}
      clearLabel={clearLabel}
      defaultOpen={variant !== 'drawer'}
      selectedCount={selectedCount}
      isOpen={isOpen}
      onToggle={onToggle}
    >
      {content}
    </FacetAccordion>
  );
};
